/* eslint-disable camelcase */
import { Button, SelectInput, useForm } from '@rocketmakers/armstrong';
import * as React from 'react';
import { DeviceType } from '../../../../shared/api-clients/_api';
import { useRoutePaths } from '../../../../shared/context/paths';
import { generateSelectOptions } from '../../../../shared/helpers/enumHelpers';
import { useValidatedForm } from '../../../../shared/hooks/formValidationHook';
import { InputValidator } from '../../../../shared/hooks/ValidationTypes';
import { BackButton } from '../../components/buttons/back-button';
import { Spinner } from '../../components/spinner/spinner';
import { useBeeConfig } from '../../hooks/beeConfig';
import { routePaths } from '../../routes';
import { deviceNameMapping } from '../assign-heating-zones/utils';

interface IThermostatModelForm {
  deviceType: DeviceType | 'No sensor installed';
}

export const validators: InputValidator<IThermostatModelForm>[] = [
  {
    key: 'deviceType',
    validators: [
      {
        validate: value => value === 'No sensor installed' || Object.values(DeviceType).includes(value),
        message: 'Must be a valid thermostat model',
      },
    ],
  },
];

export const TemperatureModelView: React.FunctionComponent = () => {
  const { getDevicesByType, devices, loading, removeDevice } = useBeeConfig();
  const initState = React.useMemo(() => {
    // currently there is only expected to be one sensor
    const [sensor] = getDevicesByType([DeviceType.WRF04, DeviceType.REGIN, DeviceType.NOVOS3]);
    return {
      deviceType: sensor?.deviceType,
    };
  }, [devices]);

  const { DataForm, bind, dataBinder } = useForm<IThermostatModelForm>(initState);
  const { setTouched, formIsValid, validationErrors } = useValidatedForm<IThermostatModelForm>(validators, dataBinder);
  const { push } = useRoutePaths();

  const handleContinue = () => {
    const deviceType = dataBinder.getValue('deviceType');

    if (Object.values(DeviceType).includes(deviceType)) {
      push(routePaths.thermostat.thermostatConfiguration, { deviceType });
    } else {
      getDevicesByType([DeviceType.WRF04, DeviceType.REGIN, DeviceType.NOVOS3]).forEach(device => removeDevice(device));
      push(routePaths.heatingSystem.root);
    }
  };

  const options = [
    { id: 'No sensor installed', name: 'No sensor installed' },
    ...generateSelectOptions(DeviceType, false, DeviceType.WRF04, DeviceType.NOVOS3, DeviceType.REGIN).map(s => {
      return { ...s, name: deviceNameMapping[s.id] };
    }),
  ];

  return (
    <DataForm validationResults={validationErrors} validationMode="both">
      {loading && <Spinner />}
      <BackButton />

      <SelectInput
        label="What is the make and model of the sensor?"
        options={options}
        {...bind.select('deviceType')}
        onBlur={setTouched}
        data-qa="select-device-type"
      />

      <div className="row">
        <Button
          className="continue-button btn-full btn-large"
          disabled={!formIsValid}
          onClick={handleContinue}
          data-qa="continue-button"
        >
          Continue
        </Button>
      </div>
    </DataForm>
  );
};
